<template>
    <v-card
        class="elevation-0 mb-4 ml-lg-n2"
        width="89vw"
        max-width="450px"
    >
        <v-card-text
            v-if="!(
                showConfirmation || $store.state.hideTransferInstructions || order.type === '3po'
            )"
            class="pa-6"
        >
            <v-row>
                <v-col v-if="!showCarousel" class="py-0">
                    <div v-if="firstSlide">
                        <div class="text-center">
                            <v-img
                                src="https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/almost-done.jpg"
                                height="170"
                                max-width="200"
                                class="mx-auto"
                            />
                            <p class="subtitle-1 mb-0 mt-2">
                                You’re almost done!
                            </p>
                        </div>
                        <div class="d-flex mt-3" v-if="isUsedBefore">
                            <v-icon class="mr-5 mt-n5" color="green">mdi-check-circle-outline</v-icon>
                            <p class="subtitle-2 mb-0">
                                For optimal performance, please change the email address on file with {{ company.name }} to
                                your Kaoshinetwork email
                            </p>
                        </div>

                        <template v-if="!isUsedBefore">
                            <div class="d-flex mt-3">
                                <v-icon class="mr-5 mt-n5" color="green">mdi-check-circle-outline</v-icon>
                                <p class="subtitle-2 mb-0" v-if="!isUsedBefore">
                                    You will be redirected to the sign up page of {{ company.name }} to create an account
                                </p>
                                <p class="subtitle-2 mb-0" v-else>
                                    You will be redirected to {{ company.name }}
                                    to make payment to your matched peer's domiciliary account in Nigeria.
                                </p>
                            </div>

                            <div class="d-flex mt-1">
                                <v-icon class="mr-5 mt-n5" color="green">mdi-check-circle-outline</v-icon>
                                <p class="subtitle-2 mb-0">
                                    <span class="orange--text darken-2">VERY IMPORTANT:</span>
                                    Use the email shown below to {{ isUsedBefore ? 'login' : ' create an account' }}
                                    on {{ company.name }}:
                                </p>
                            </div>
                        </template>

                        <div class="d-flex justify-center mt-3 ml-8 pb-6">
                            <zelle-payment :recipientInfo="{ email: $store.state.user.kaoshi_email }" />
                        </div>

                        <div class="d-flex mt-3" v-if="isUsedBefore">
                            <v-icon class="mr-5 mt-n5" color="green">mdi-check-circle-outline</v-icon>
                            <p class="subtitle-2 mb-0">
                                To change this email address, visit the settings page
                                on your {{ company.name }} app and update this email address
                            </p>
                        </div>

                        <div class="text-center text-md-right mt-3">
                            <v-btn
                                class="text-capitalize px-10 rounded-lg"
                                color="primary"
                                dark
                                depressed
                                large
                                @click.native="firstSlide = false"
                            >
                                Continue
                            </v-btn>
                        </div>
                    </div>

                    <div v-else>
                        <div class="text-center">
                            <v-img
                                src="https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/todo.jpg"
                                height="200"
                                max-width="250"
                                class="mb-3 mx-auto"
                            />
                        </div>

                        <v-alert type="warning" class="body-2 mb-0 my-3 px-6" text dense>
                            Kindly ensure you perform the next set of instructions to ensure
                            your transaction on {{ company.name }} operates smoothly
                        </v-alert>

                        <div class="text-right mt-6">
                            <v-btn
                                class="text-capitalize px-10 rounded-lg"
                                color="primary"
                                dark
                                depressed
                                large
                                @click.native="setShowCarousel"
                            >
                                Proceed
                            </v-btn>
                        </div>
                    </div>
                </v-col>

                <SendwaveLogin
                    v-if="showCarousel && isUsedBefore && companyCode === 'sendwave'"
                    @proceed="showConfirmation = true"
                />
                <v-col
                    v-if="showCarousel && isUsedBefore && companyCode !== 'sendwave'"
                    class="py-0"
                    :class="[{ 'px-0 px-lg-3': currentSlide === totalSlides-1 }]"
                >
                    <v-carousel
                        v-model="currentSlide"
                        class="mt-3"
                        :show-arrows="false"
                        height="400"
                    >
                        <v-carousel-item>
                            <div>
                                <div class="text-center">
                                    <v-img
                                        src="https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/3po-login.jpg"
                                        height="220"
                                        max-width="220"
                                        class="mb-3 mx-auto"
                                    />
                                </div>

                                <div class="d-flex mt-8 justify-center">
                                    <v-icon class="mr-5" color="green">mdi-check-circle-outline</v-icon>
                                    <p class="subtitle-1 mb-0">
                                        Login to {{ company.name }}. Select Nigeria
                                    </p>
                                </div>
                            </div>
                        </v-carousel-item>

                        <v-carousel-item>
                            <div>
                                <div class="text-center">
                                    <v-img
                                        :src="get3poDeliveryMethodImage(company)"
                                        contain
                                        class="mb-3 mx-auto"
                                    />
                                </div>

                                <div class="d-flex mt-8">
                                    <v-icon class="mr-5" color="green">mdi-check-circle-outline</v-icon>
                                    <p class="subtitle-1 mb-0">
                                        {{ get3poDeliverMethodText(company) }}
                                    </p>
                                </div>
                            </div>
                        </v-carousel-item>

                        <v-carousel-item>
                            <div>
                                <div class="text-center">
                                    <v-img
                                        :src="get3poYouSendImage(company)"
                                        contain
                                        class="mb-3 mx-auto"
                                    />
                                </div>

                                <div class="d-flex mt-8" v-if="company && company.name">
                                    <v-icon class="mr-5 mt-n5" color="green">mdi-check-circle-outline</v-icon>
                                    <p class="subtitle-1 mb-0" v-if="company.name === 'World Remit'">
                                        <span class="orange--text darken-2">VERY IMPORTANT:</span>
                                        Enter <span class="font-weight-bold">{{ formattedAmount }}</span> in the "They get" field
                                    </p>
                                    <p class="subtitle-1 mb-0" v-if="company.name === 'Remitly'">
                                        <span class="orange--text darken-2">VERY IMPORTANT:</span>
                                        Ensure that the "They receive" field shows <span class="font-weight-bold">{{ formattedAmount }}</span>
                                    </p>
                                </div>
                            </div>
                        </v-carousel-item>

                        <v-carousel-item v-if="isRemitly">
                            <div>
                                <div class="text-center">
                                    <v-img
                                        src="/images/remitly-delivery-speed.png"
                                        max-width="400"
                                        class="mb-3 mx-auto"
                                    />
                                </div>

                                <div class="d-flex mt-8">
                                    <v-icon class="mr-5" color="green">mdi-check-circle-outline</v-icon>
                                    <p class="subtitle-1 mb-0">
                                        Select "Express" as the preferred Delivery speed
                                    </p>
                                </div>
                            </div>
                        </v-carousel-item>

                        <v-carousel-item>
                            <div id="scroller" class="mr-n3 mr-lg-0">
                                <div class="text-center">
                                    <v-img
                                        src="https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/transaction.jpg"
                                        contain
                                        class="mb-3 mx-auto"
                                    />
                                </div>

                                <div class="d-flex mt-5">
                                    <v-icon class="mr-5 mt-n5" color="green">mdi-check-circle-outline</v-icon>
                                    <p class="subtitle-1 mb-0">
                                        Kindly make payment to the matched peer with the following details:
                                    </p>
                                </div>

                                <v-sheet
                                    tile
                                    color="#f6f6f6"
                                    class="py-6 px-3 px-lg-4 grey--text text--darken-3 my-3 mr-lg-3 rounded-lg"
                                >
                                    <v-row class="position-relative">
                                        <mono-payment :recipientInfo="recipientInfo" />
                                    </v-row>
                                </v-sheet>
                            </div>
                        </v-carousel-item>
                    </v-carousel>

                    <v-row
                        :class="[{ 'mt-5 mt-lg-3': currentSlide !== totalSlides-1 }]"
                    >
                        <v-col
                            class="col-lg-6"
                            :class="[!!currentSlide ? 'text-center text-md-left col-4': 'text-left']"
                        >
                            <v-btn
                                class="text-capitalize"
                                text
                                large
                                v-if="currentSlide !== totalSlides-1"
                                @click="currentSlide = totalSlides - 1"
                            >
                                Skip
                            </v-btn>
                        </v-col>

                        <v-col
                            class="d-flex justify-md-end col-lg-6"
                            :class="[!!currentSlide ? 'justify-center' : 'justify-end']"
                        >
                            <v-btn
                                class="text-capitalize px-5 rounded-lg mr-3"
                                color="amber"
                                text
                                large
                                v-if="currentSlide > 0"
                                @click="currentSlide--"
                            >
                                Prev
                            </v-btn>
                            <v-btn
                                class="text-capitalize px-5 rounded-lg"
                                color="primary"
                                dark
                                depressed
                                large
                                @click.prevent="currentSlide++"
                                v-if="currentSlide < totalSlides-1"
                            >
                                Next
                            </v-btn>
                            <v-btn
                                v-else
                                class="text-capitalize px-5 rounded-lg"
                                color="primary"
                                dark
                                depressed
                                large
                                @click="showConfirmation = true"
                            >
                                Proceed
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>

                <WorldremitRegister
                    v-if="showCarousel && !isUsedBefore && companyCode === 'worldremit'"
                    @proceed="showConfirmation = true"
                />

                <RemitlyRegister
                    v-if="showCarousel && !isUsedBefore && companyCode === 'remitly'"
                    @proceed="showConfirmation = true"
                />
            </v-row>
        </v-card-text>

        <third-party-offer-guide
            v-else
            @proceed="$emit('proceed', {
              url: companyUrl,
            })"
            :company="company"
            :company_logo="company.logo"
            :orderType="order.type"
            :matcherCurrency="order.matcher_currency"
        />
    </v-card>
</template>

<script>
    import { replace } from 'lodash';
    let currency = require('../../../modules/currency.js');
    import ThirdPartyOfferGuide from './3po-guide.vue';
    import MonoPayment from '../../match-confirmed-reveal-type-mono-payment';
    import ZellePayment from '../../match-confirmed-reveal-type-zelle-payment';
    import WorldremitRegister from './instructions/worldremit-register.vue';
    import RemitlyRegister from './instructions/remitly-register.vue';
    import SendwaveLogin from './instructions/sendwave-login.vue';

    export default {
        data() {
            return {
                showConfirmation: false,
                showCarousel: false,
                currentSlide: 0,
                firstSlide: true,
            };
        },
        components: {
            ThirdPartyOfferGuide,
            MonoPayment,
            ZellePayment,
            WorldremitRegister,
            RemitlyRegister,
            SendwaveLogin,
        },
        props: {
            recipientInfo: {
                type: Object,
                required: true,
            },
            order: {
                type: Object,
                required: true,
            },
            isUsedBefore: {
                type: Boolean,
                required: true,
            },
        },
        computed: {
            company() {
                return this.order.company;
            },
            companyCode() {
                const { company } = this.order;
                if (company.name) {
                    return company.name.toLowerCase().replace(' ', '');
                }

                return null
            },
            formattedAmount() {
                let amount = this.order.eer + this.order.matcher_amount;
                let orderCurrency = this.order.matcher_currency;

                if (this.order.type === 'hybrid') {
                    amount = parseFloat(this.order.pb_amount) + parseFloat(this.order.matcher_fee);
                    orderCurrency = this.order.pb_currency;
                }

                return `${currency.getCurrencySymbol(orderCurrency)}${amount.toFixed(2)}`;
            },
            isRemitly() {
                const { company } = this.order;

                if (!company) {
                    return false;
                }

                if (!company.name) {
                    return false;
                }

                return company.name === 'Remitly';
            },
            totalSlides() {
                return this.isRemitly ? 5 : 4;
            },
            companyUrl() {
                let {
                    login_page: loginPage,
                    signup_page: signupPage,
                } = this.company;

                if (this.isRemitly) {
                    // Replace for correct user's country
                    const countryAbbreviation = this.$_user.country_abbreviation || 'us';
                    signupPage = replace(signupPage, /\/us\//gm, `/${countryAbbreviation.toLowerCase()}/`);
                }

                return this.isUsedBefore ? loginPage : signupPage;
            }
        },
        methods: {
            setShowCarousel () {
                if (this.companyCode === 'sendwave' && !this.isUsedBefore) {
                    this.showConfirmation = true
                    return
                }

                this.showCarousel = true
            },

            get3poDeliveryMethodImage(company) {
                const companyName = company ? company.name : null;
                switch (companyName) {
                case 'World Remit':
                    return 'https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/world-remit1.jpg';
                case 'Remitly':
                    return 'https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/remitly-1.png';
                default:
                    return '';
                }
            },

            get3poDeliverMethodText(company) {
                const companyName = company ? company.name : null;
                switch (companyName) {
                case 'World Remit':
                    return 'Change it from “Cash Pickup” to “Bank Transfer”';
                case 'Remitly':
                    return 'Select "Bank Deposit" as the preferred Delivery Method';
                default:
                    return '';
                }
            },

            get3poYouSendImage(company) {
                const companyName = company ? company.name : null;
                switch (companyName) {
                case 'World Remit':
                    return 'https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/world-remit2.jpg';
                case 'Remitly':
                    return 'https://s3.amazonaws.com/files.kaoshinetwork.com/illustrations/remitly-2.png';
                default:
                    return '';
                }
            },
        },
    }
</script>

<style scoped>
#scroller {
    height: 45vh;
}

.actions {
    position: absolute;
    bottom: 20px;
    width: 90%;
}

.actions button {
    z-index: 9;
}

.v-carousel >>> .v-carousel__controls {
    position: absolute;
    bottom: -5px;
}

.v-carousel >>> .v-carousel__controls__item.v-btn.v-item--active::before,
.v-carousel >>> .v-carousel__controls {
    background-color: transparent;
}
.v-carousel >>> .v-carousel__controls__item .v-icon {
    opacity: 1;
}
/* v-item--activev-btn--active.v-btn--icon */
.v-carousel >>> .v-carousel__controls__item.v-btn {
    color: #c4c4c4;
}

.v-carousel >>> .v-carousel__controls__item.v-btn.v-item--active {
    color: #5DAE6F;
}

.position-relative >>> .copy-button {
    right: 15px
}
</style>
